import { z } from 'zod';
import { LIST_PAGE_SIZE } from '../../config.ts';
import { VENUES } from '../../ontology/calendar.ts';
import { INSTRUMENT_KINDS, MARKETS, OPTION_TYPES } from '../../ontology/instrument.ts';
import { FILING_SOURCES } from '../../ontology/objects/filing.ts';
import { RECOMMENDATION_STATUSES } from '../../ontology/objects/recommendation.ts';
import { idSchema, TRADE_TYPES } from '../../ontology/primitives.ts';
import { indicatedOpenSchema } from './indication.ts';
import { day, instant, monthMoveSchema } from './primitives.ts';

export const CLOSED_RECOS_CAP = LIST_PAGE_SIZE;

export const wireRecommendationSchema = z.object({
  id: idSchema,
  externalId: z.string().nullable(),
  bookId: idSchema,
  book: z.string(),
  analystId: idSchema,
  underlying: z.string(),
  tradeType: z.enum(TRADE_TYPES),
  status: z.enum(RECOMMENDATION_STATUSES),
  recommendedOn: day,
  createdAt: instant,
  // The tracked contract alone decides whether the recommendation is over.
  trackingToken: z.number().int(),
  trackingTradingsymbol: z.string(),
  trackingExchange: z.enum(VENUES),
  trackingKind: z.enum(INSTRUMENT_KINDS),
  trackingTickSize: z.number(),
  entryPrice: z.number(),
  targetPrice: z.number(),
  stoplossPrice: z.number(),
  filedStoplossPrice: z.number(),
  filedPrice: z.number().nullable(),
  closedPrice: z.number().nullable(),
  closedAt: instant.nullable(),
  closedText: z.string().nullable(),
  closedLink: z.string().nullable(),
});

export type WireRecommendation = z.infer<typeof wireRecommendationSchema>;

/**
 * Never capped: RoI covers the whole record (as `listRecommendationsForRoi`).
 * Closed rows go to every viewer; open ones only to the desk and the analyst's readers.
 */
export const wireRoiRecommendationSchema = wireRecommendationSchema
  .pick({
    id: true,
    bookId: true,
    analystId: true,
    underlying: true,
    status: true,
    tradeType: true,
    recommendedOn: true,
    trackingToken: true,
    trackingTradingsymbol: true,
    trackingExchange: true,
    entryPrice: true,
    targetPrice: true,
    stoplossPrice: true,
    filedStoplossPrice: true,
    closedPrice: true,
  })
  .extend({ trackingExpiry: day.nullable(), closedOn: day.nullable() });

export type WireRoiRecommendation = z.infer<typeof wireRoiRecommendationSchema>;

const wireRsiSchema = z.object({
  closed: z.object({ value: z.number(), at: instant }).nullable(),
  live: z.number().nullable(),
  candles: z.number().int(),
  cools: z.object({ LONG: z.number().nullable(), SHORT: z.number().nullable() }),
});

export type WireRsi = z.infer<typeof wireRsiSchema>;

export const wireQuoteSchema = z.object({
  token: z.number().int(),
  price: z.number(),
  asOf: instant,
  day: z.object({ high: z.number(), low: z.number() }).nullable(),
  /** The previous session's close, which the day's move is measured from; absent until Kite has said it. */
  close: z.number().optional(),
  rsi: wireRsiSchema.nullable(),
});

export type WireQuote = z.infer<typeof wireQuoteSchema>;

export const wireLegSchema = z.object({
  token: z.number().int(),
  tradingsymbol: z.string(),
  exchange: z.string(),
  kind: z.enum(INSTRUMENT_KINDS),
  contract: z.string(),
  expiry: day.nullable(),
  lotSize: z.number().int(),
  price: z.number().nullable(),
  tracked: z.boolean(),
});

const wireTradeableLegSchema = wireLegSchema.extend({
  tickSize: z.number().nullable(),
  option: z.object({ strike: z.number(), optionType: z.enum(OPTION_TYPES) }).nullable(),
});

export type WireTradeableLeg = z.infer<typeof wireTradeableLegSchema>;

const wireCandleSchema = z.object({
  day,
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
});

const wireFiledSchema = z.object({
  source: z.enum(FILING_SOURCES),
  link: z.string().nullable(),
  text: z.string().nullable(),
  at: instant.nullable(),
  setup: z.object({ strategy: z.string(), interval: z.string().nullable() }).nullable(),
  reviewed: z.boolean().nullable(),
  drawn: z.object({ tradingsymbol: z.string(), basis: z.number() }).nullable(),
});

export type WireFiled = z.infer<typeof wireFiledSchema>;

export const recommendationDetailSchema = z.object({
  recommendation: wireRecommendationSchema,
  analyst: z.object({ id: idSchema, name: z.string() }),
  market: z.enum(MARKETS),
  exchange: z.string(),
  expiry: day.nullable(),
  riskBudget: z.number().nullable(),
  filed: wireFiledSchema,
  ground: z
    .object({
      last: z.number().nullable(),
      day: z.object({ high: z.number(), low: z.number() }).nullable(),
      ranges: z.array(z.object({ label: z.string(), high: z.number(), low: z.number() })),
    })
    .nullable(),
  path: z.array(wireCandleSchema),
  rsi: z.array(z.object({ at: instant, value: z.number() })),
  legs: z.array(wireTradeableLegSchema),
  indicated: indicatedOpenSchema.nullable(),
  asOf: instant,
});

export type RecommendationDetail = z.infer<typeof recommendationDetailSchema>;

export const recommendationRolloverSchema = monthMoveSchema.extend({
  recommendationId: idSchema,
  outPrice: z.number().nullable(),
  inPrice: z.number().nullable(),
});

export type RecommendationRollover = z.infer<typeof recommendationRolloverSchema>;

export const deskReadSchema = z.discriminatedUnion('available', [
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    read: z.object({
      thesis: z.string(),
      watch: z.array(z.string()).max(3),
      legs: z.array(z.object({ name: z.string(), suits: z.string() })),
      caution: z.string().nullable(),
    }),
    model: z.string(),
    at: instant,
  }),
]);

export type DeskRead = z.infer<typeof deskReadSchema>;

export const bookSettingsSchema = z.object({
  description: z.string().nullable(),
  limitMarginBps: z.number().int().nullable(),
  optionLimitMarginBps: z.number().int().nullable(),
  takesOptions: z.boolean(),
  takesCommodities: z.boolean(),
  takesUnnamed: z.boolean(),
  archived: z.boolean(),
});

/**
 * Index is a book's colour: keep `listBooks` order and never re-sort in the browser.
 * Books are per analyst; two analysts' `equity` books are distinct entries.
 */
export const bookOrderSchema = z.array(
  z.object({
    bookId: idSchema,
    analystId: idSchema,
    analyst: z.string(),
    book: z.string(),
    ...bookSettingsSchema.shape,
  }),
);

export type WireBook = z.infer<typeof bookOrderSchema>[number];

export const wireAnalystRefSchema = z.object({ id: idSchema, name: z.string() });

export type WireAnalystRef = z.infer<typeof wireAnalystRefSchema>;
